/* global globalThis */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Description,
  School,
  AttachFile,
  PictureAsPdf,
  Image,
  PhotoCamera,
} from '@mui/icons-material';

let documentIdCounter = 0;
const generateSecureDocumentId = () => {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.getRandomValues) {
    const randomBytes = new Uint32Array(2);
    globalThis.crypto.getRandomValues(randomBytes);
    return `${Date.now()}-${randomBytes[0].toString(16)}${randomBytes[1].toString(16)}`;
  }

  documentIdCounter += 1;
  return `${Date.now()}-${documentIdCounter}`;
};

const Step8Dokumen = ({ form, setForm }) => {
  const [internalDocuments, setInternalDocuments] = useState(() => {
    return Array.isArray(form.documents) ? form.documents : [];
  });

  // Sync internal state with parent form state
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      documents: internalDocuments
    }));
  }, [internalDocuments, setForm]);

  const handleFileUpload = (event, documentType) => {
    const files = event.target.files;
    if (files.length > 0) {
      const file = files[0];
      
      // Create file preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const newDocument = {
          id: generateSecureDocumentId(),
          type: documentType,
          name: file.name,
          file: file,
          preview: e.target.result,
          size: file.size,
          uploadDate: new Date().toISOString()
        };

        setInternalDocuments(prev => {
          // Remove existing document of same type
          const filtered = prev.filter(doc => doc.type !== documentType);
          return [...filtered, newDocument];
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentRemove = (documentId) => {
    setInternalDocuments(prev => prev.filter(doc => doc.id !== documentId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) {
      return <PictureAsPdf sx={{ color: '#d32f2f', fontSize: 40 }} />;
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
      return <Image sx={{ color: '#1976d2', fontSize: 40 }} />;
    } else {
      return <Description sx={{ color: '#757575', fontSize: 40 }} />;
    }
  };

  const getDocumentByType = (type) => {
    return internalDocuments.find(doc => doc.type === type);
  };

  const documentTypes = [
    {
      type: 'pas_foto',
      title: 'Pas Foto',
      description: 'Upload pas foto terbaru (4x6 cm, background merah atau biru)',
      icon: <PhotoCamera sx={{ fontSize: 24, color: '#1976d2' }} />,
      accept: '.jpg,.jpeg,.png',
      maxSize: 2 * 1024 * 1024, // 2MB
      isPhoto: true,
    },
    {
      type: 'ijazah',
      title: 'Ijazah Terakhir',
      description: 'Upload ijazah pendidikan terakhir (SMA, D3, S1, S2, dll)',
      icon: <School sx={{ fontSize: 24, color: '#1976d2' }} />,
      accept: '.pdf,.jpg,.jpeg,.png',
      maxSize: 5 * 1024 * 1024, // 5MB
    },
    {
      type: 'cv',
      title: 'Curriculum Vitae (CV)',
      description: 'Upload CV dalam format PDF atau DOC',
      icon: <AttachFile sx={{ fontSize: 24, color: '#1976d2' }} />,
      accept: '.pdf,.doc,.docx',
      maxSize: 3 * 1024 * 1024, // 3MB
    }
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#1976d2' }}>
        📎 Upload Dokumen Wajib
      </Typography>
      
      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>WAJIB:</strong> Silakan upload dokumen-dokumen wajib berikut untuk melanjutkan proses rekrutmen: 
        <strong>Pas Foto, Ijazah Terakhir, dan CV</strong>. 
        Pastikan dokumen yang diupload jelas dan dapat terbaca dengan baik.
      </Alert>

      <Grid container spacing={3}>
        {documentTypes.map((docType) => {
          const document = getDocumentByType(docType.type);
          
          return (
            <Grid item xs={12} md={6} key={docType.type}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {docType.icon}
                    <Box sx={{ ml: 2, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                          {docType.title}
                        </Typography>
                        <Chip 
                          label="WAJIB" 
                          color="error" 
                          size="small" 
                          sx={{ 
                            fontSize: '0.7rem', 
                            fontWeight: 'bold',
                            height: '20px'
                          }} 
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {docType.description}
                      </Typography>
                    </Box>
                  </Box>

                  {document ? (
                    <Box>
                      <Box sx={{ 
                        border: '2px dashed #1976d2', 
                        borderRadius: 2, 
                        p: 3, 
                        textAlign: 'center',
                        backgroundColor: '#f5f5f5',
                        position: 'relative'
                      }}>
                        {docType.isPhoto ? (
                          <Box>
                            <img 
                              src={document.preview} 
                              alt="Pas Foto" 
                              style={{
                                width: '120px',
                                height: '160px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid #1976d2',
                                marginBottom: '8px'
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {document.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {formatFileSize(document.size)}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            {getFileIcon(document.name)}
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                              {document.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {formatFileSize(document.size)}
                            </Typography>
                          </Box>
                        )}
                        <Chip 
                          label="Terupload" 
                          color="success" 
                          size="small" 
                          sx={{ mt: 1 }}
                        />
                        
                        <IconButton
                          onClick={() => handleDocumentRemove(document.id)}
                          sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8,
                            backgroundColor: '#f44336',
                            color: 'white',
                            '&:hover': { backgroundColor: '#d32f2f' }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <input
                        type="file"
                        id={`file-upload-${docType.type}`}
                        accept={docType.accept}
                        onChange={(e) => handleFileUpload(e, docType.type)}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor={`file-upload-${docType.type}`}>
                        <Box
                          sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: '#1976d2',
                              backgroundColor: '#f5f5f5'
                            }
                          }}
                        >
                          <CloudUpload sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#666' }}>
                            Klik untuk upload file
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            Format: {docType.accept} (Max: {formatFileSize(docType.maxSize)})
                          </Typography>
                        </Box>
                      </label>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Dokumen Tambahan */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
          Dokumen Tambahan (Opsional)
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Upload dokumen pendukung lainnya seperti sertifikat, portofolio, atau dokumen relevan lainnya.
        </Typography>

        <Card elevation={1}>
          <CardContent>
            <input
              type="file"
              id="additional-docs-upload"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                files.forEach(file => {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    const newDocument = {
                      id: generateSecureDocumentId(),
                      type: 'additional',
                      name: file.name,
                      file: file,
                      preview: event.target.result,
                      size: file.size,
                      uploadDate: new Date().toISOString()
                    };
                    setInternalDocuments(prev => [...prev, newDocument]);
                  };
                  reader.readAsDataURL(file);
                });
              }}
              style={{ display: 'none' }}
            />
            <label htmlFor="additional-docs-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                sx={{ mb: 2 }}
              >
                Upload Dokumen Tambahan
              </Button>
            </label>

            {internalDocuments.filter(doc => doc.type === 'additional').length > 0 && (
              <List>
                {internalDocuments
                  .filter(doc => doc.type === 'additional')
                  .map((doc) => (
                    <ListItem key={doc.id} divider>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                        {getFileIcon(doc.name)}
                      </Box>
                      <ListItemText
                        primary={doc.name}
                        secondary={formatFileSize(doc.size)}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleDocumentRemove(doc.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Status Validasi Dokumen */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
          Status Dokumen Wajib
        </Typography>
        <Card elevation={1}>
          <CardContent>
            {['pas_foto', 'ijazah', 'cv'].map((docType) => {
              const hasDocument = internalDocuments.some(doc => doc.type === docType);
              const docName = docType === 'pas_foto' ? 'Pas Foto' : 
                             docType === 'ijazah' ? 'Ijazah Terakhir' : 'Curriculum Vitae (CV)';
              
              return (
                <Box key={docType} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label={hasDocument ? '✓ Terupload' : '✗ Belum Upload'} 
                    color={hasDocument ? 'success' : 'error'} 
                    size="small" 
                    sx={{ minWidth: 120, mr: 2 }}
                  />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {docName}
                  </Typography>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Box>

      {/* Informasi Penting */}
      <Alert severity="warning" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Penting:</strong> Pastikan semua dokumen yang diupload adalah asli dan valid. 
          Dokumen palsu atau tidak valid dapat menyebabkan pembatalan proses rekrutmen.
        </Typography>
      </Alert>
    </Box>
  );
};

export default Step8Dokumen;
